import type { MantineTheme } from '@mantine/core';
import { cssVariablesResolver } from '../theme/theme';

/**
 * Keeping the browser chrome in step with a forced colour scheme.
 *
 * `index.html` ships two media-qualified `theme-color` tags, so the OS decides which one the
 * browser honours. That is right by default — and wrong the moment the learner forces Light on
 * a dark phone, because the tag being honoured is still the dark one.
 *
 * The values come from `theme.ts` rather than being written out again here: it is the design's
 * source of truth and this milestone may not edit it, so reading it is the only way the two
 * cannot drift. (`cssVariablesResolver` ignores its argument — it returns a literal.)
 */
const vars = cssVariablesResolver({} as MantineTheme);

export const THEME_COLORS = {
  dark: vars.dark['--nn-bg'] as string,
  light: vars.light['--nn-bg'] as string,
} as const;

export type SchemeChoice = 'auto' | 'dark' | 'light';

/**
 * What each `theme-color` tag's content should be for a given choice.
 *
 * When a scheme is forced, BOTH tags get that colour. Patching only the currently-matching one
 * would look right until the phone flipped to night mode with Light still forced, and then the
 * chrome would go dark under a light app.
 */
export function themeColorPatch(choice: SchemeChoice): { dark: string; light: string } {
  if (choice === 'auto') return { dark: THEME_COLORS.dark, light: THEME_COLORS.light };
  const forced = THEME_COLORS[choice];
  return { dark: forced, light: forced };
}

/** Write the patch onto the two tags. A no-op anywhere the tags are absent. */
export function applyThemeColor(choice: SchemeChoice, doc: Document = document): void {
  const patch = themeColorPatch(choice);
  const set = (media: 'dark' | 'light', value: string) => {
    doc
      .querySelector(`meta[name="theme-color"][media*="${media}"]`)
      ?.setAttribute('content', value);
  };
  set('dark', patch.dark);
  set('light', patch.light);
}
